<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/Website_Integration';
    let nextLink= '/Examples/Website_Integration/Including_Model_Information';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="getting-language-information-1"
  bind:intersecting={visibleById["getting-language-information-1"]}
  onIntersectChange={(visible) => setVisible("getting-language-information-1", visible)}
>Getting Language Information</SectionComponent>
<p>There are a couple of places where we would like to use information, not about the models, but about the language for which the editor was created.
We need the names of the available projections for use in the view menu, and we will use the names of the model unit types in the drawer that
shows the available units in the model. Just for fun, we would like to show the name of the language in the navbar.</p>
<p>Because the last is fairly simple, we start by making the change in the navbar.</p>
<SectionComponent tag="h2" 
  id="language-name-in-the-navbar-2"
  bind:intersecting={visibleById["language-name-in-the-navbar-2"]}
  onIntersectChange={(visible) => setVisible("language-name-in-the-navbar-2", visible)}
>Language Name in the Navbar</SectionComponent>
<p>To include information from the DSL we use a Svelte state variable called <code>langInfo</code>.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/stores/LanguageInfo.svelte.ts

export interface LanguageInfo &#123;
  // name of the language
  name: string;
  // names of all model unit types
  unitTypes: string[];
  // all known file extensions
  fileExtensions: string[];
  // all possible projections
  projectionNames: string[];
&#125;

export const langInfo: LanguageInfo = $state(&#123;
  name: "FreLanguage ...",
  unitTypes: [] as string[],
  fileExtensions: [] as string[],
  projectionNames: [] as string[],
&#125;);
</code></pre>
<p>We use this state variable in the NavBar on line 26:</p>
<pre class="language-html"><code class="language-html">// IntegrationExample/webapp/src/lib/main-app/NavBar.svelte#L24-L27

&lt;NavBrand href="/"&gt;
	&lt;img src="./freonlogo.svg" class="me-3 h-6 sm:h-9" alt="Freon Logo" /&gt;
	&lt;span class="self-center whitespace-nowrap text-xl font-semibold dark:text-white"&gt;Freon for &#123;langInfo.name&#125;&lt;/span&gt;
&lt;/NavBrand&gt;</code></pre>
<p>We would like to say: “that’s it”, but so far there is no information present in the state variable.</p>
<SectionComponent tag="h2" 
  id="initialization-of-the-state-variable-3"
  bind:intersecting={visibleById["initialization-of-the-state-variable-3"]}
  onIntersectChange={(visible) => setVisible("initialization-of-the-state-variable-3", visible)}
>Initialization of the State Variable</SectionComponent>
<p>Before the start of the web app we need to initialize the <code>langInfo</code> state variable. We have chosen to do this
in a Svelte hook (see <a href="https://svelte.dev/docs/kit/hooks" target="_blank">SvelteKit Hooks</a>), because it needs to be done only once. We
use the <code>configureExternals</code> and <code>configureLoggers</code> functions from the DSL, as well as the <code>LanguageEnvironment</code>.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/hooks.client.ts

/*
 * This files contains all initialization that needs to be done ONCE at the startup of the Freon application
 */
import &#123;
  configureExternals,
  configureLoggers,
  LanguageEnvironment,
&#125; from "@freon4dsl/samples-education";
import &#123; WebappConfigurator &#125; from "$lib/language/WebappConfigurator.js";
import &#123; ServerCommunication &#125; from "@freon4dsl/core";

WebappConfigurator.getInstance().setEnvironment(
  LanguageEnvironment.getInstance(),
  ServerCommunication.getInstance(),
);
configureExternals();
configureLoggers();
</code></pre>
<p>As you can see we have created a file called <code>WebappConfigurator</code> to contain all code that deals with
the DSL and with the <code>@freon4dsl/core</code> package. Because we use the server that Freon provides (also as a convenience),
we can use an instance of <code>ServerCommunication</code> from <code>@freon4dsl/core</code>. In a production environment this
needs to be replaced with your own server implementation. Note that any server must implement
the <code>IServerCommunication</code> interface.</p>
<p>The <code>setEnvironment</code> function remembers the object that will perform the communication with the server, and
the language environment, for further use. For the core package to function properly, you also need to
create an instance of the class <code>InMemoryModel</code>, which also takes the language environment and the server
implementation as parameters. The <code>InMemoryModel</code> handles creation, saving, etc. of models and model units.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/language/WebappConfigurator.ts#L45-L54

setEnvironment(
    editorEnvironment: FreEnvironment,
    serverCommunication: IServerCommunication,
): void &#123;
    // LOGGER.log('setEnvironment')
    this.editorEnvironment = editorEnvironment;
    this.serverCommunication = serverCommunication;
    WebappConfigurator.initialize(editorEnvironment);
    this.modelStore = new InMemoryModel(editorEnvironment, serverCommunication);
&#125;</code></pre>
<p>Finally, the <code>initialize</code> function fills the Svelte state variable <code>langInfo</code> with the values that we need.
Note that the <code>LanguageEnvironment</code> initializes the <code>FreLanguage</code> class from the core package. A call to
<code>LanguageEnvironment.getInstance()</code> must therefore precede a call to <code>FreLanguage.getInstance()</code>. Both classes
follow the Singleton pattern and are initialized through <code>getInstance</code>.</p>
<p>Commented out, because we do not yet have an element in our web app to show error messages, is the way to
handle messages from the Freon editor. Assign a function to the editor’s <code>setUserMessage</code> property, which handles
the messages. Its signature is <code>setUserMessage(message: string, severity?: FreErrorSeverity): void</code>.</p>
<p>Note also that you need to start the <code>FreUndoManager</code> for undo/redo handling to function.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/language/WebappConfigurator.ts#L60-L92

static initialize(editorEnvironment: FreEnvironment): void &#123;
    let langEnv: FreEnvironment = editorEnvironment;
    // the language name
    langInfo.name = langEnv.languageName;

    // the names of the unit types
    langInfo.unitTypes = FreLanguage.getInstance().getUnitNames();

    // the names of the projections / views
    const proj: FreProjectionHandler = langEnv.editor.projection;
    let nameList: string[] = !!proj ? proj.projectionNames() : ["default"];
    // remove any old values
    langInfo.projectionNames.splice(0, langInfo.projectionNames.length);
    // push the right ones
    langInfo.projectionNames.push(...nameList);
    replaceProjectionsShown(nameList);

    // the file extensions for all unit types
    // because 'langEnv.fileExtensions.values()' is not an Array but an IterableIterator,
    // we transfer the value to a tmp array.
    const tmp: string[] = [];
    for (const val of langEnv.fileExtensions.values()) &#123;
        tmp.push(val);
    &#125;
    langInfo.fileExtensions = tmp;

    // let the editor know how to set the user message,
    // we do this by assigning our own method to the editor's method
    // langEnv.editor.setUserMessage = setUserMessage;

    // start the undo manager
    FreUndoManager.getInstance();
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="unit-types-in-model-drawer-4"
  bind:intersecting={visibleById["unit-types-in-model-drawer-4"]}
  onIntersectChange={(visible) => setVisible("unit-types-in-model-drawer-4", visible)}
>Unit Types in Model Drawer</SectionComponent>
<p>In this example web app, as in the standard Freon web app, we show the information about
the current model in a drawer panel.
For the names of the model unit types in this drawer we use the same approach as above. From the
<code>langInfo</code> state variable we take the <code>unitTypes</code>, and loop over them. The inner list is where
the model information</p>


<PrevNextSection {prevLink} {nextLink} />